import {
  KNOWLEDGE_KINDS,
  KNOWLEDGE_SCOPES,
  type KnowledgeKind,
  type KnowledgeListItem,
  type KnowledgeScope,
} from '@fos/shared/contracts/knowledge';
import { KNOWLEDGE_KIND_PLURAL_LABELS } from '@fos/shared/labels';
import { BookOpen, Plus, Search, SlidersHorizontal } from 'lucide-react';
import { type ReactNode, useDeferredValue, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Button, IconButton } from '../../../ui/Button';
import { Field } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { Select } from '../../../ui/Select';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { ClientPicker } from '../../clients/components/ClientPicker';
import { MobileFab, useIsPhone } from '../../mobile';
import { ProjectPicker } from '../../projects/components/ProjectPicker';
import { groupByClient, itemSubtitle } from '../knowledge-format';
import { canExportMarkdown, ExportMarkdownDialog } from './ExportMarkdownDialog';

const ALL = 'all';
const SCOPE_FILTER_LABELS: Record<KnowledgeScope, string> = {
  workspace: 'Firma',
  client: 'Klienci',
  project: 'Projekty',
};

export interface KnowledgeFilters {
  kind: KnowledgeKind | null;
  scope: KnowledgeScope | null;
  clientId: string | null;
  projectId: string | null;
  archived: boolean;
}

/** Filtry z adresu (`?rodzaj=decision&zakres=project&klient=<id>&projekt=<id>&archiwum=1`). */
export function filtersFromParams(params: URLSearchParams): KnowledgeFilters {
  const kind = params.get('rodzaj');
  const scope = params.get('zakres');
  return {
    kind: (KNOWLEDGE_KINDS as readonly string[]).includes(kind ?? '') ? (kind as KnowledgeKind) : null,
    scope: (KNOWLEDGE_SCOPES as readonly string[]).includes(scope ?? '') ? (scope as KnowledgeScope) : null,
    clientId: params.get('klient'),
    projectId: params.get('projekt'),
    archived: params.get('archiwum') === '1',
  };
}

function activeFilterCount(f: KnowledgeFilters): number {
  return [f.kind, f.scope, f.clientId, f.projectId, f.archived || null].filter(Boolean).length;
}

function ItemLink({
  item,
  active,
  now,
  search,
}: {
  item: KnowledgeListItem;
  active: boolean;
  now: Date;
  search: string;
}) {
  return (
    <Link
      to={`/wiedza/${item.id}${search ? `?${search}` : ''}`}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex flex-col rounded-lg px-3 py-2 transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
        active ? 'bg-elevated' : 'hover:bg-surface',
      )}
    >
      <span className={cn('truncate text-body-s text-fg', active ? 'font-semibold' : 'font-medium')}>{item.title}</span>
      <span className="truncate text-caption text-fg-muted">{itemSubtitle(item, now)}</span>
    </Link>
  );
}

function ExtraRow({
  title,
  subtitle,
  to,
  onClick,
}: {
  title: string;
  subtitle: string;
  to?: string;
  onClick?: () => void;
}) {
  const classes =
    'flex w-full flex-col rounded-lg px-3 py-2 text-left transition-colors hover:bg-surface focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent';
  const content = (
    <>
      <span className="truncate text-body-s font-medium text-fg">{title}</span>
      <span className="truncate text-caption text-fg-muted">{subtitle}</span>
    </>
  );
  return to ? (
    <Link to={to} className={classes}>
      {content}
    </Link>
  ) : (
    <button type="button" onClick={onClick} className={classes}>
      {content}
    </button>
  );
}

/**
 * Lista Wiedzy (lewy panel Figmy 10): tytuł z „+”, wyszukiwanie w tytułach i treści, filtry (rodzaj, zakres,
 * klient, projekt, archiwum — w adresie), grupy: firma (z profilami OWNER.md i BUSINESS.md dla właściciela,
 * admina i managera), potem klienci (z „Kontekst klienta · CLIENT.md” przy „Eksportuje dane”).
 */
export function KnowledgeBrowser({
  selectedId,
  workspaceLabel,
  onNew,
  headingLevel = 'h1',
}: {
  selectedId: string | null;
  workspaceLabel: string;
  onNew?: () => void;
  headingLevel?: 'h1' | 'h2';
}) {
  const access = useAccess();
  const [params, setParams] = useSearchParams();
  const filters = filtersFromParams(params);
  const [q, setQ] = useState('');
  const query = useDeferredValue(q.trim());
  const [filtersOpen, setFiltersOpen] = useState(() => activeFilterCount(filters) > 0);
  const phone = useIsPhone();
  const [exportClient, setExportClient] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const now = new Date();
  const search = params.toString();

  const list = trpc.knowledge.list.useQuery({
    ...(filters.kind ? { kind: filters.kind } : {}),
    ...(filters.scope ? { scope: filters.scope } : {}),
    ...(filters.clientId ? { clientId: filters.clientId } : {}),
    ...(filters.projectId ? { projectId: filters.projectId } : {}),
    ...(filters.archived ? { archived: true } : {}),
    ...(query.length >= 2 ? { q: query } : {}),
  });
  const plain = activeFilterCount(filters) === 0 && query.length < 2;
  const overview = trpc.context.overview.useQuery(undefined, { enabled: access.isManagerOrAbove && plain });
  const exportsClients = plain && canExportMarkdown(access, 'client');

  const setFilter = (key: string, value: string | null) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (value) p.set(key, value);
        else p.delete(key);
        if (key === 'klient') p.delete('projekt');
        return p;
      },
      { replace: true },
    );

  const Heading = headingLevel;
  const count = activeFilterCount(filters);

  let body: ReactNode;
  if (list.isPending) body = <ListSkeleton rows={6} className="px-1" />;
  else if (list.isError)
    body = (
      <ErrorState
        compact
        title="Nie udało się wczytać wiedzy"
        onRetry={() => void list.refetch()}
        retrying={list.isFetching}
      />
    );
  else {
    const groups = groupByClient(list.data, workspaceLabel);
    const profiles = overview.data?.profiles ?? [];
    const firm = groups[0];
    const clientGroups = groups.slice(1);
    const nothing = list.data.length === 0 && profiles.length === 0;
    if (nothing) {
      body = plain ? (
        <EmptyState
          icon={BookOpen}
          title="Brak notatek"
          description="Notatki, decyzje, linki i pliki klientów i projektów pojawią się tutaj."
          action={
            onNew ? (
              <Button variant="secondary" icon={Plus} onClick={onNew}>
                Nowa notatka
              </Button>
            ) : undefined
          }
          className="py-10"
        />
      ) : (
        <EmptyState icon={Search} title="Nic nie pasuje" description="Zmień frazę albo filtry." className="py-10" />
      );
    } else {
      body = (
        <div className="flex flex-col gap-5">
          {firm && (firm.items.length > 0 || profiles.length > 0) && (
            <section aria-label={firm.label}>
              <h3 className="px-3 pb-1 text-caption text-fg-muted">{firm.label}</h3>
              <ul>
                {profiles.map((p) => (
                  <li key={p.kind}>
                    <ExtraRow
                      title={p.kind === 'owner' ? 'Profil właściciela' : 'Oferta i stawki'}
                      subtitle={p.versionNo > 0 ? `${p.file} · v${p.versionNo}` : `${p.file} · pusty`}
                      to={`/ustawienia/ai#${p.kind}`}
                    />
                  </li>
                ))}
                {firm.items.map((item) => (
                  <li key={item.id}>
                    <ItemLink item={item} active={item.id === selectedId} now={now} search={search} />
                  </li>
                ))}
              </ul>
            </section>
          )}
          {clientGroups.map((g) => (
            <section key={g.key} aria-label={g.label}>
              <h3 className="px-3 pb-1 text-caption text-fg-muted">{g.label}</h3>
              <ul>
                {g.items.map((item) => (
                  <li key={item.id}>
                    <ItemLink item={item} active={item.id === selectedId} now={now} search={search} />
                  </li>
                ))}
                {exportsClients && g.clientId && (
                  <li>
                    <ExtraRow
                      title="Kontekst klienta"
                      subtitle="CLIENT.md · generowany z bieżących danych"
                      onClick={() => {
                        setExportClient(g.clientId);
                        setExportOpen(true);
                      }}
                    />
                  </li>
                )}
              </ul>
            </section>
          ))}
        </div>
      );
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <Heading className="min-w-0 flex-1 text-h2 text-fg md:text-h3">Wiedza</Heading>
        <IconButton
          icon={SlidersHorizontal}
          label={count > 0 ? `Filtry (${count})` : 'Filtry'}
          size={32}
          iconSize={18}
          aria-expanded={filtersOpen}
          aria-controls="knowledge-filters"
          onClick={() => setFiltersOpen((v) => !v)}
          className={cn('max-md:size-11', count > 0 && 'bg-accent-subtle text-accent')}
        />
        {/* telefon: „Nowa notatka” pod kciukiem (MobileFab, lifting 17.10.2026) */}
        {onNew && !phone && <IconButton icon={Plus} label="Nowa notatka" size={32} iconSize={18} onClick={onNew} />}
        {onNew && phone && (
          <MobileFab icon={Plus} onClick={onNew}>
            Nowa notatka
          </MobileFab>
        )}
      </div>
      <Input
        icon={Search}
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Szukaj w notatkach i decyzjach"
        aria-label="Szukaj w wiedzy"
        className="max-md:h-11"
      />
      {filtersOpen && (
        <div
          id="knowledge-filters"
          className="grid grid-cols-1 gap-3 rounded-lg border border-line bg-panel p-3 md:border-0 md:bg-surface"
        >
          <Field label="Rodzaj">
            <Select
              size="s"
              value={filters.kind ?? ALL}
              onValueChange={(v) => setFilter('rodzaj', v === ALL ? null : v)}
              options={[
                { value: ALL, label: 'Wszystkie rodzaje' },
                ...KNOWLEDGE_KINDS.map((k) => ({ value: k, label: KNOWLEDGE_KIND_PLURAL_LABELS[k] })),
              ]}
            />
          </Field>
          <Field label="Zakres">
            <Select
              size="s"
              value={filters.scope ?? ALL}
              onValueChange={(v) => setFilter('zakres', v === ALL ? null : v)}
              options={[
                { value: ALL, label: 'Wszystko' },
                ...KNOWLEDGE_SCOPES.map((s) => ({ value: s, label: SCOPE_FILTER_LABELS[s] })),
              ]}
            />
          </Field>
          <Field label="Klient">
            <ClientPicker
              size="s"
              allowNone
              noneLabel="Wszyscy klienci"
              value={filters.clientId}
              onChange={(id) => setFilter('klient', id)}
            />
          </Field>
          <Field label="Projekt">
            <ProjectPicker
              size="s"
              clientId={filters.clientId ?? undefined}
              emptyLabel="Wszystkie projekty"
              value={filters.projectId}
              onValueChange={(id) => setFilter('projekt', id)}
            />
          </Field>
          <Field label="Pokaż">
            <Select
              size="s"
              value={filters.archived ? 'archived' : 'current'}
              onValueChange={(v) => setFilter('archiwum', v === 'archived' ? '1' : null)}
              options={[
                { value: 'current', label: 'Aktualne' },
                { value: 'archived', label: 'Archiwum' },
              ]}
            />
          </Field>
          {count > 0 && (
            <Button
              variant="ghost"
              size="s"
              onClick={() =>
                setParams(
                  (prev) => {
                    const p = new URLSearchParams(prev);
                    for (const key of ['rodzaj', 'zakres', 'klient', 'projekt', 'archiwum']) p.delete(key);
                    return p;
                  },
                  { replace: true },
                )
              }
            >
              Wyczyść filtry
            </Button>
          )}
        </div>
      )}
      {/* telefon: lista w białym bloku na szarym tle; desktop: cała kolumna listy jest biała */}
      <div className="-mx-3 max-md:mx-0 max-md:rounded-xl max-md:border max-md:border-line max-md:bg-panel max-md:p-2">
        {body}
      </div>
      {exportClient && (
        <ExportMarkdownDialog kind="client" id={exportClient} open={exportOpen} onOpenChange={setExportOpen} />
      )}
    </div>
  );
}
